import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { validateRedirect } from "@njbsoft/utils";
import { useApiClient } from "@/lib/auth";
import { router } from "@/router";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import { MfaChallenge } from "@/components/mfa-challenge";
import { switchToOrg } from "@/lib/org-switch";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Runtime-safe preferences extraction replacing unsafe `as` chains */
function extractPreferences(
  raw: unknown,
): { theme?: string; locale?: string; accent_color?: string } | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  return {
    theme: typeof obj.theme === "string" ? obj.theme : undefined,
    locale: typeof obj.locale === "string" ? obj.locale : undefined,
    accent_color: typeof obj.accent_color === "string" ? obj.accent_color : undefined,
  };
}

interface LoginSearch {
  redirect?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const api = useApiClient();
  const setAuth = useAppStore((s) => s.setAuth);
  const setMfaChallenge = useAppStore((s) => s.setMfaChallenge);
  const clearMfaChallenge = useAppStore((s) => s.clearMfaChallenge);
  const mfaPending = useAppStore((s) => s.mfaPending);
  const { redirect } = Route.useSearch();
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const completeLogin = async (data: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
    user: { id: string; email: string; role: string; email_verified: boolean };
    preferences?: { theme?: string; locale?: string; accent_color?: string };
  }) => {
    setAuth(
      data.access_token,
      {
        id: data.user.id,
        email: data.user.email,
        role: data.user.role,
        emailVerified: data.user.email_verified,
      },
      data.refresh_token,
    );
    // A sign-in's token names no org, so an org an earlier session left in the
    // persisted store is stale from this moment: the picker's guard and the
    // shell would both trust it while every org-scoped read is refused.
    useAppStore.getState().clearActiveOrg();
    if (data.expires_at) {
      useAppStore.getState().setExpiresAt(new Date(data.expires_at).getTime());
    }
    // Sync preferences using runtime validation instead of unsafe `as` chains
    const prefs = data.preferences;
    if (prefs) {
      const theme = prefs.theme;
      if (theme === "system" || theme === "light" || theme === "dark") {
        useAppStore.getState().setThemePreference(theme);
      }
      const locale = prefs.locale;
      if (locale === "system" || locale === "en" || locale === "es") {
        useAppStore.getState().setLocalePreference(locale);
      }
      if (typeof prefs.accent_color === "string" && prefs.accent_color) {
        useAppStore.getState().setAccentColor(prefs.accent_color);
      }
    }
    clearMfaChallenge();
    await router.invalidate();

    // Post-login org branching
    try {
      const { data: orgsData } = await api.GET("/orgs/me");
      const orgs = orgsData?.orgs ?? [];

      if (orgs.length === 0) {
        void navigate({ to: "/" });
      } else if (orgs.length === 1 && orgs[0]) {
        const org = orgs[0];
        await switchToOrg({
          orgId: org.id,
          orgName: org.name,
          orgRole: org.role,
          orgSlug: org.slug,
          requireMfa: org.require_mfa ?? false,
        });
        void navigate({ to: validateRedirect(redirect) ?? "/" });
      } else {
        // 2+ orgs: redirect to selection page
        void navigate({ to: "/select-org", search: { redirect } });
      }
    } catch {
      // If org fetch fails, proceed to the home route with no org context
      void navigate({ to: "/" });
    }
  };

  const loginMutation = useMutation({
    mutationFn: async (creds: { email: string; password: string }) => {
      const { data, error } = await api.POST("/auth/login", {
        body: creds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      // Check if MFA is required
      if (data.mfa_required && data.mfa_session_token && data.mfa_methods) {
        setMfaChallenge(data.mfa_session_token, data.mfa_methods);
        return;
      }

      // No MFA required -- proceed with normal login
      if (data && data.access_token && data.refresh_token && data.user) {
        await completeLogin({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at ?? "",
          user: {
            id: data.user.id,
            email: data.user.email,
            role: data.user.role,
            email_verified: data.user.email_verified,
          },
          // Runtime validate preferences instead of unsafe `as` cast
          preferences: extractPreferences(data.user.preferences),
        });
      }
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      showApiError(code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const email = (formData.get("email") as string).trim();
    const password = formData.get("password") as string;

    const errors: Record<string, string> = {};
    if (!email) {
      errors.email = t("validation.required");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("validation.invalidEmail");
    }
    if (!password) {
      errors.password = t("validation.required");
    } else if (password.length < 8) {
      errors.password = t("validation.minLength", { min: 8 });
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    loginMutation.mutate({ email, password });
  };

  const handleMfaSuccess = (data: {
    access_token: string;
    refresh_token: string;
    expires_at: string;
    user: { id: string; email: string; role: string; email_verified: boolean };
  }) => {
    void completeLogin(data);
  };

  const handleMfaCancel = () => {
    clearMfaChallenge();
  };

  // Show MFA challenge if MFA is pending
  if (mfaPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface">
        <MfaChallenge onSuccess={handleMfaSuccess} onCancel={handleMfaCancel} />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <div>
          <h2 className="text-center text-3xl font-bold text-on-surface">{t("signInTitle")}</h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-muted">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-muted">
                {t("passwordLabel")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-danger">{fieldErrors.password}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-primary hover:text-primary-hover"
            >
              {t("forgotPassword")}
            </Link>
          </div>

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            {loginMutation.isPending ? t("signingIn") : t("signIn")}
          </button>
        </form>

        <p className="text-center text-sm text-muted">
          {t("noAccount")}{" "}
          <Link to="/signup" className="font-medium text-primary hover:text-primary-hover">
            {t("signUpLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
